import { Link, useLocation, useNavigate } from "react-router-dom";
import { HiMenu, HiCheckCircle } from "react-icons/hi";
import { FiLogOut, FiUser, FiSettings, FiUsers } from "react-icons/fi";
import { UserRole } from "@models/auth.types";
import { useAuth } from "@hooks/useAuth";
import { useUnsavedChanges } from "@contexts/UnsavedChangesContext";
import NotificationBell from "./NotificationBell/NotificationBell";
import Logo from "../assets/images/monumenta.png";

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasRole } = useAuth();
  const { guardNavigation } = useUnsavedChanges();

  // Navegação protegida: se houver alterações não salvas, pede confirmação antes.
  const navegarComGuarda = (
    e: React.MouseEvent,
    proceed: () => void,
  ) => {
    e.preventDefault();
    guardNavigation(proceed);
  };

  const handleLogout = () => {
    guardNavigation(async () => {
      await logout();
      navigate("/login");
    });
  };

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const navLinks = [
    { path: "/planilhas", label: "Planilhas" },
    { path: "/clientes", label: "Clientes" },
    { path: "/fornecedores", label: "Fornecedores" },
  ];

  // Verificar se está na rota de editar planilha
  const isEditarPlanilhaRoute =
    location.pathname.startsWith("/planilhas/editar/") ||
    location.pathname.startsWith("/planilhas-nova/editar/");

  return (
    <div
      className={`navbar bg-base-100 shadow-md border-b border-base-300 backdrop-blur-sm bg-opacity-95 ${
        isEditarPlanilhaRoute ? "" : "sticky top-0 z-50"
      }`}
    >
      <div className="navbar-start">
        <Link
          to="/"
          onClick={(e) => navegarComGuarda(e, () => navigate("/"))}
          className="flex items-center gap-2 px-4 hover:opacity-80 transition-opacity"
        >
          <div className="flex items-center justify-center w-12 rounded-lg">
            <img src={Logo} alt="Logo" />
          </div>
          <div className="flex flex-col">
            <h1 className="text-lg font-bold text-base-content leading-tight">
              Sistema de Cotação
            </h1>
            <span className="text-xs text-base-content opacity-70 leading-tight">
              MONUMENTA
            </span>
          </div>
        </Link>
      </div>

      <div className="navbar-end">
        {/* Menu Desktop */}
        <ul className="menu menu-horizontal px-1 gap-1 hidden lg:flex">
          {navLinks.map((link) => {
            const active = isActive(link.path);
            return (
              <li key={link.path}>
                <Link
                  to={link.path}
                  onClick={(e) =>
                    navegarComGuarda(e, () => navigate(link.path))
                  }
                  className={`
                    px-4 py-2 rounded-lg font-medium transition-all duration-200 relative
                    ${
                      active
                        ? "bg-primary text-primary-content shadow-md"
                        : "text-base-content hover:bg-base-200 hover:text-primary"
                    }
                  `}
                >
                  {link.label}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* User info e logout - Desktop */}
        {user && (
          <div className="hidden lg:flex items-center gap-2 ml-4">
            <NotificationBell />
            <div className="dropdown dropdown-end">
              <div
                tabIndex={0}
                role="button"
                className="btn btn-ghost btn-circle avatar"
              >
                {user.avatarUrl ? (
                  <div className="w-10 rounded-full">
                    <img src={user.avatarUrl} alt={user.nome} />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                    <FiUser className="h-5 w-5 text-primary-content" />
                  </div>
                )}
              </div>
              <ul
                tabIndex={0}
                className="menu menu-sm dropdown-content mt-3 z-10 p-2 shadow-xl bg-base-100 rounded-box w-64 border border-base-300"
              >
                <li className="menu-title">
                  <span className="text-black font-semibold">{user.nome}</span>
                </li>
                <li className="menu-title">
                  <span className="text-xs opacity-70">{user.email}</span>
                </li>
                <li className="mt-2">
                  <Link
                    to="/perfil"
                    onClick={(e) => navegarComGuarda(e, () => navigate("/perfil"))}
                  >
                    <FiSettings className="h-4 w-4" />
                    Meu perfil
                  </Link>
                </li>
                {hasRole(UserRole.ADMIN) && (
                  <li>
                    <Link
                      to="/admin/usuarios"
                      onClick={(e) =>
                        navegarComGuarda(e, () => navigate("/admin/usuarios"))
                      }
                    >
                      <FiUsers className="h-4 w-4" />
                      Usuários e permissões
                    </Link>
                  </li>
                )}
                <li>
                  <button
                    onClick={handleLogout}
                    className="text-red-500 hover:bg-error hover:text-error-content"
                  >
                    <FiLogOut className="h-4 w-4" />
                    Sair
                  </button>
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Sino de notificações - Mobile */}
        {user && (
          <div className="lg:hidden">
            <NotificationBell />
          </div>
        )}

        {/* Menu Mobile */}
        <div className="lg:hidden dropdown dropdown-end">
          <div
            tabIndex={0}
            role="button"
            className="btn btn-ghost btn-circle mr-2"
          >
            <HiMenu className="h-6 w-6" />
          </div>
          <ul
            tabIndex={0}
            className="menu menu-sm dropdown-content mt-3 z-10 p-2 shadow-xl bg-base-100 rounded-box w-56 border border-base-300"
          >
            {user && (
              <>
                <li className="menu-title">
                  <span className="text-sm font-semibold">{user.nome}</span>
                </li>
                <li className="menu-title">
                  <span className="text-xs opacity-70">{user.email}</span>
                </li>
                <div className="divider my-1"></div>
              </>
            )}
            {navLinks.map((link) => {
              const active = isActive(link.path);
              return (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    onClick={(e) =>
                      navegarComGuarda(e, () => navigate(link.path))
                    }
                    className={`
                      ${active ? "bg-primary text-primary-content" : ""}
                    `}
                  >
                    {link.label}
                    {active && <HiCheckCircle className="h-4 w-4 ml-auto" />}
                  </Link>
                </li>
              );
            })}
            {user && (
              <>
                <div className="divider my-1"></div>
                <li>
                  <Link
                    to="/perfil"
                    onClick={(e) => navegarComGuarda(e, () => navigate("/perfil"))}
                  >
                    <FiSettings className="h-4 w-4" />
                    Meu perfil
                  </Link>
                </li>
                {hasRole(UserRole.ADMIN) && (
                  <li>
                    <Link
                      to="/admin/usuarios"
                      onClick={(e) =>
                        navegarComGuarda(e, () => navigate("/admin/usuarios"))
                      }
                    >
                      <FiUsers className="h-4 w-4" />
                      Usuários e permissões
                    </Link>
                  </li>
                )}
                <li>
                  <button
                    onClick={handleLogout}
                    className="text-error hover:bg-error hover:text-error-content"
                  >
                    <FiLogOut className="h-4 w-4" />
                    Sair
                  </button>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
